"use client";

import React, { useState, useEffect } from "react";
import { X, ShieldCheck, CheckCircle2, ArrowRight, Activity, FileText } from "lucide-react";
import Link from "next/link";
import { INSURANCE_PRODUCTS } from "@/lib/demos/insuranceData";

interface QuoteProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  currency: "INR" | "USD";
  initialData?: {
    productId: string;
    productName: string;
    sumAssured: number;
    annualPremium: number;
    age: number;
    isTobaccoUser: boolean;
  } | null;
}

export default function QuoteProposalModal({
  isOpen,
  onClose,
  currency,
  initialData,
}: QuoteProposalModalProps) {
  const [prospectName, setProspectName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [age, setAge] = useState(32);
  const [isTobaccoUser, setIsTobaccoUser] = useState(false);
  const [productId, setProductId] = useState("aegis-term-shield");
  const [coverageAmount, setCoverageAmount] = useState(20000000);
  const [annualPremium, setAnnualPremium] = useState(18500);
  const [nomineeName, setNomineeName] = useState("");
  const [nomineeRelation, setNomineeRelation] = useState("Spouse");

  const [submitting, setSubmitting] = useState(false);
  const [submittedProposal, setSubmittedProposal] = useState<any | null>(null);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (initialData) {
      setProductId(initialData.productId);
      setCoverageAmount(initialData.sumAssured);
      setAnnualPremium(initialData.annualPremium);
      setAge(initialData.age);
      setIsTobaccoUser(initialData.isTobaccoUser);
    }
  }, [initialData]);

  if (!isOpen) return null;

  const currentProduct =
    INSURANCE_PRODUCTS.find((p) => p.id === productId) || INSURANCE_PRODUCTS[0];
  const sym = currency === "USD" ? "$" : "₹";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prospectName || !email) {
      setErrorMsg("Please enter your name and email address.");
      return;
    }

    setSubmitting(true);
    setErrorMsg("");

    try {
      const res = await fetch("/api/demos/insurance/policies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prospect_name: prospectName,
          email,
          phone,
          age,
          is_tobacco_user: isTobaccoUser,
          product_id: productId,
          product_name: currentProduct.name,
          coverage_amount: coverageAmount,
          annual_premium: annualPremium,
          tenure_years: 30,
          nominee_name: nomineeName || "Primary Legal Heir",
          nominee_relation: nomineeRelation,
          currency,
        }),
      });

      const data = await res.json();
      if (data.success && data.policy) {
        setSubmittedProposal(data.policy);
      } else {
        setErrorMsg(data.error || "Failed to submit proposal");
      }
    } catch (err: any) {
      console.error(err);
      setErrorMsg("Network error submitting proposal");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setSubmittedProposal(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md overflow-y-auto">
      <div className="bg-slate-900 border border-blue-900/60 rounded-2xl w-full max-w-xl overflow-hidden shadow-2xl relative my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-slate-950 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600/20 border border-blue-500/40 flex items-center justify-center text-blue-400">
              <ShieldCheck className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">
                {submittedProposal ? "Proposal Case Ingested" : "Lock Indicative Quote & e-KYC"}
              </h3>
              <p className="text-xs text-slate-400">
                Aegis Prime Wealth &amp; Insurance • IRDAI Underwriting Desk
              </p>
            </div>
          </div>
          <button
            onClick={handleResetForm}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success View */}
        {submittedProposal ? (
          <div className="p-6 sm:p-8 space-y-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-950/80 border border-emerald-600/60 flex items-center justify-center mx-auto text-emerald-400">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded bg-blue-950 text-blue-300 border border-blue-800/80">
                Case No: {submittedProposal.proposal_number}
              </span>
              <h4 className="text-xl font-extrabold text-white mt-3">
                Proposal Successfully Ingested!
              </h4>
              <p className="text-xs sm:text-sm text-slate-300 mt-2 max-w-md mx-auto">
                An official <strong>Proposal Illustration Email</strong> has been dispatched to{" "}
                <span className="text-blue-400 font-medium">{submittedProposal.email}</span> via Hostinger SMTP.
              </p>
            </div>

            {/* Ingested Details Card */}
            <div className="p-4 rounded-xl bg-slate-950/90 border border-slate-800 text-left text-xs space-y-2">
              <div className="flex justify-between border-b border-slate-800/80 pb-2">
                <span className="text-slate-400">Life Assured:</span>
                <span className="text-white font-semibold">{submittedProposal.prospect_name} ({submittedProposal.age} Yrs)</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/80 pb-2">
                <span className="text-slate-400">Selected Plan:</span>
                <span className="text-white font-semibold">{submittedProposal.product_name}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/80 pb-2">
                <span className="text-slate-400">Sum Assured:</span>
                <span className="text-blue-400 font-bold">{sym}{submittedProposal.coverage_amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between border-b border-slate-800/80 pb-2">
                <span className="text-slate-400">Indicative Premium:</span>
                <span className="text-white font-bold">{sym}{submittedProposal.annual_premium.toLocaleString()} / year</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Nominee Registered:</span>
                <span className="text-emerald-400 font-semibold">{submittedProposal.nominee_name} ({submittedProposal.nominee_relation})</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Link
                href="/demos/insurance/crm"
                className="flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold shadow-lg shadow-blue-950 transition-all"
              >
                <Activity className="w-4 h-4 text-emerald-400" />
                <span>Track in Underwriting CRM</span>
              </Link>
              <button
                type="button"
                onClick={handleResetForm}
                className="py-3 px-5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          /* Intake Form */
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {errorMsg && (
              <div className="p-3 rounded-lg bg-rose-950/80 border border-rose-800/80 text-rose-300 text-xs">
                {errorMsg}
              </div>
            )}

            {/* Plan Overview Pill */}
            <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
              <div>
                <div className="text-[11px] text-slate-400">Proposed Coverage Plan</div>
                <div className="text-xs font-bold text-white mt-0.5">{currentProduct.name}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-emerald-400 font-bold">{currentProduct.taxBenefit}</div>
                <div className="text-xs font-extrabold text-blue-400 mt-0.5">
                  {sym}{coverageAmount.toLocaleString()} Sum Assured
                </div>
              </div>
            </div>

            {/* Name & Email */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Full Name (As per PAN/Aadhaar) *
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Vikram Malhotra"
                  value={prospectName}
                  onChange={(e) => setProspectName(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Official Email Address *
                </label>
                <input
                  type="email"
                  required
                  placeholder="e.g. vikram@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Phone & Age */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Mobile Number (For OTP e-KYC)
                </label>
                <input
                  type="tel"
                  placeholder="+91 98765 43210"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-300 block mb-1">
                  Applicant Age (Years)
                </label>
                <input
                  type="number"
                  min={18}
                  max={65}
                  value={age}
                  onChange={(e) => setAge(Number(e.target.value))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Nominee Details */}
            <div className="p-3 rounded-xl bg-slate-950/70 border border-slate-800/80 space-y-3">
              <div className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
                Designated Nominee &amp; Beneficiary
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-slate-400 block mb-1">
                    Nominee Full Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Sneha Malhotra"
                    value={nomineeName}
                    onChange={(e) => setNomineeName(e.target.value)}
                    className="w-full px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-slate-400 block mb-1">
                    Relationship
                  </label>
                  <select
                    value={nomineeRelation}
                    onChange={(e) => setNomineeRelation(e.target.value)}
                    className="w-full px-3 py-1.5 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white focus:outline-none focus:border-blue-500"
                  >
                    <option value="Spouse">Spouse</option>
                    <option value="Child">Child</option>
                    <option value="Parent">Parent</option>
                    <option value="Sibling">Sibling</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white text-xs font-bold shadow-lg shadow-blue-900/50 transition-all cursor-pointer disabled:opacity-50"
              >
                {submitting ? (
                  <span>Ingesting Proposal &amp; Incurring e-KYC...</span>
                ) : (
                  <>
                    <span>Submit Proposal &amp; Trigger Underwriting Ingestion</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-[10px] text-center text-slate-400 mt-2">
                🔒 Data secured via 256-bit SSL encryption. Auto-dispatches Proposal Illustration via Hostinger SMTP.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
